import { useAppDispatch, useAppSelector } from "@/app/reduxHooks";
import PickerDetailsHeader from "@/features/adp/components/shared/PickerDetailsHeader";
import { ResolvedIcon } from "@/features/adp/components/shared/ResolvedIcon";
import { useRegisteredSources } from "@/features/adp/queries/useSourceQueries";
import { DataSourceWithExtras } from "@/features/adp/services/common/common.types";
import { RegisteredSourceDataSource } from "@/features/adp/services/source/source.types";
import { closeOverlayAsync } from "@/features/adp/state/overlay.thunks";
import { openOverlay, PickerOverlayData } from "@/features/adp/state/overlaySlice";
import { Button } from "@/ui/button";
import { Card } from "@/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/ui/sheet";
import { Skeleton } from "@/ui/skeleton";
import { useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import styles from "./SourceSheets.module.scss";

const DEFAULT_LIMIT = 6;

function buildRegisteredSourceValues(source: RegisteredSourceDataSource): Record<string, string> {
    const values: Record<string, string> = {};
    const assign = (key: string, value: unknown) => {
        if (value == null) return;
        const stringValue = String(value);
        if (stringValue.length === 0) return;
        values[key] = stringValue;
    };

    // Typed RDBMS fields first — these are the common shape.
    assign('source_name', source.data_source_name);
    assign('database', source.database_name);
    assign('database_name', source.database_name);
    assign('username', source.username);
    assign('password', source.password);
    assign('host', source.endpoint);
    assign('endpoint', source.endpoint);
    assign('secret_key', source.secret_key);
    assign('secret_key2', source.secret_key2);

    // Non-RDBMS fields the API may return for S3/Snowflake/BigQuery/etc.
    // The type doesn't list these, but the backend returns them when present.
    // Without copying them through, `submitted_source_payload` for a registered
    // S3 source loses `bucket_name`, a Snowflake loses `warehouse`, etc., and
    // the subsequent `/connections/columns` call fails or auths against the
    // wrong region.
    const sourceAsRecord = source as unknown as Record<string, unknown>;
    const PASS_THROUGH_KEYS = [
        'bucket_name',
        'warehouse',
        'role',
        'region',
        'ssl_mode',
        'auth_method',
        'auth_type',
        'project_id',
        'dataset',
        'account',
        'cluster',
        'connection_uri',
        'path',
        'file_delimiter',
        'date_format',
    ] as const;
    for (const key of PASS_THROUGH_KEYS) {
        assign(key, sourceAsRecord[key]);
    }

    return values;
}

const SourcePicker = () => {
    const { isOpen, type, data } = useAppSelector(state => state.overlay);
    const sourcePickerData = data as PickerOverlayData;
    const isSourcePickerOpen = isOpen && type === 'source-picker';
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();
    const sourceData: DataSourceWithExtras[] = queryClient.getQueryData(['sidebar-data'])?.[0];
    const selectedSource = isSourcePickerOpen ? sourceData?.find(source => source?.data_source_id === Number(sourcePickerData?.sourceId)) : null;

    const { data: registeredSourcesResponse, isPending } = useRegisteredSources({
        allowed_instances: selectedSource?.data_source_instance,
        enabled: isSourcePickerOpen,
        page: 1,
        limit: DEFAULT_LIMIT,
    });
    const registeredSources = registeredSourcesResponse?.sources ?? [];
    const totalCount = registeredSourcesResponse?.total_count ?? 0;
    const hasMore = totalCount > DEFAULT_LIMIT;

    const handleOpenChange = (open: boolean) => {
        if (!open) {
            dispatch(closeOverlayAsync());
        }
    };

    const handleNewSourceClick = () => {
        dispatch(openOverlay({
            type: 'source-configuration',
            data: {
                nodeId: sourcePickerData?.nodeId,
                ...selectedSource,
            }
        }));
    }

    const handleViewMoreClick = () => {
        dispatch(openOverlay({
            type: 'source-registeration-modal',
            data: {
                nodeId: sourcePickerData?.nodeId,
                sourceId: sourcePickerData?.sourceId,
                sourceType: sourcePickerData?.sourceType,
                ...selectedSource,
            }
        }));
    }

    const handleRegisteredSourceClick = (source: RegisteredSourceDataSource) => {
        const registeredSourceValues = buildRegisteredSourceValues(source);

        dispatch(openOverlay({
            type: 'source-registered-tables-list',
            data: {
                nodeId: sourcePickerData?.nodeId,
                sourceName: source.data_source_name,
                databaseName: source.database_name,
                schemaName: null,
                sourceDataSourceId: source.source_data_source_id ?? null,
                rooId: source.roo_id ?? null,
                sourceInstance: source.data_source_instance ?? selectedSource?.data_source_instance ?? null,
                dataSourceType: source.data_source_type ?? selectedSource?.data_source_type ?? null,
                registeredSourceValues,
                pickerSourceId: sourcePickerData?.sourceId ?? null,
                pickerSourceType: sourcePickerData?.sourceType ?? null,
            }
        }));
    }

    return (
        <Sheet open={isSourcePickerOpen} onOpenChange={handleOpenChange}>
            <SheetContent side="right" noBackdrop topOffset={64} className="shadow-none">
                <SheetHeader>
                    <SheetTitle className="text-[18px] font-medium">Source Details</SheetTitle>
                </SheetHeader>
                <div className={`flex h-full flex-col gap-4 overflow-y-auto px-3 ${styles.thinScroll}`}>
                    <PickerDetailsHeader
                        type="source"
                        prerequisite={selectedSource?.prerequisite}
                        imageLink={selectedSource?.image_link}
                        sourceName={selectedSource?.data_source_name}
                        label="Source Name"
                    />

                    <p className="text-sm text-muted-foreground">
                        Select a recent existing source or add a new one to begin configuring and analyzing your data quality.
                    </p>

                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {isPending &&
                            ["1", "2", "3", "4", "5", "6"].map((skeletonId) => (
                                <Card key={`source-skeleton-${skeletonId}`} className="border p-3 shadow-none">
                                    <div className="flex items-center gap-3">
                                        <Skeleton className="h-9 w-9 rounded-md" />
                                        <div className="flex-1 space-y-2">
                                            <Skeleton className="h-4 w-24" />
                                            <Skeleton className="h-3 w-32" />
                                        </div>
                                    </div>
                                </Card>
                            ))}

                        {!isPending &&
                            registeredSources.map((source) => (
                                <Card
                                    key={source.source_data_source_id ?? source.roo_id ?? source.data_source_name}
                                    className="cursor-pointer border p-3 shadow-none transition-colors hover:border-primary/40"
                                    onClick={() => handleRegisteredSourceClick(source)}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-md border">
                                            <ResolvedIcon
                                                imageLink={source.image_link}
                                                alt={source.data_source_name}
                                                className="h-7 w-7 object-contain"
                                            />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="truncate text-sm font-semibold capitalize">
                                                {source.data_source_instance}
                                            </p>
                                            <p className="truncate text-xs text-muted-foreground">{source.data_source_name}</p>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                    </div>

                    <div className="space-y-3 pb-4">
                        {hasMore && (
                            <Button
                                type="button"
                                variant="text"
                                className="block w-full text-sm font-semibold"
                                onClick={handleViewMoreClick}
                            >
                                View more
                            </Button>
                        )}
                        <Button variant="outline" className="w-full gap-2" onClick={handleNewSourceClick}>
                            <Plus className="h-4 w-4" />
                            Add new source
                        </Button>
                    </div>
                </div>
            </SheetContent>
        </Sheet>
    );
};

export default SourcePicker;
